import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Base skeleton primitive — a neutral placeholder block with a slow, low
 * contrast shimmer. Skeletons are the calm alternative to spinners for content
 * whose shape is known ahead of time (charts, KPI cards, tables). Many of these
 * resolving at slightly different times reads as "filling in", not "chaos",
 * because the shimmer is subtle and synchronised — unlike independent spinners.
 *
 * Honours `prefers-reduced-motion` by holding a static block.
 */
const shimmer = keyframes`
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
`;

const toCss = (value: number | string): string =>
  typeof value === 'number' ? `${value}px` : value;

const Block = styled.div<{
  $width: string;
  $height: string;
  $radius: string;
}>`
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
  border-radius: ${(props) => props.$radius};
  flex-shrink: 0;
  background-color: var(--color-skeleton-base);
  background-image: linear-gradient(
    90deg,
    var(--color-skeleton-base) 0%,
    var(--color-skeleton-highlight) 50%,
    var(--color-skeleton-base) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background-image: none;
  }
`;

const Skeleton = ({
  width = '100%',
  height = '100%',
  borderRadius = 6,
  circle = false,
  className,
  style,
}: {
  width?: number | string;
  height?: number | string;
  borderRadius?: number | string;
  circle?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <Block
    className={className}
    style={style}
    data-testid="skeleton"
    $width={circle ? toCss(height) : toCss(width)}
    $height={toCss(height)}
    $radius={circle ? '50%' : toCss(borderRadius)}
  />
);

export default Skeleton;
